import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './RepoMain.module.css';
import Header from '../components/Header';
import ContactFooter from '../components/ContactFooter';

// Enhanced repository icon with modern styling
const RepoIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C13.1 2 14 2.9 14 4V6H20C21.1 6 22 6.9 22 8V20C22 21.1 21.1 22 20 22H4C2.9 22 2 21.1 2 20V8C2 6.9 2.9 6 4 6H10V4C10 2.9 10.9 2 12 2ZM12 4V6H12ZM4 8V20H20V8H4ZM7 10H17V12H7V10ZM7 14H17V16H7V14Z"></path>
    </svg>
);

// Additional icons for different types of projects
const GitIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2ZM15.995 9.51C16.009 9.52 16.021 9.531 16.034 9.543C16.046 9.555 16.057 9.567 16.071 9.578C16.434 9.853 16.563 10.34 16.434 10.747L15.273 14.224C15.145 14.631 14.671 14.874 14.264 14.746C14.044 14.674 13.877 14.507 13.805 14.287L12.917 11.474L12.029 14.287C11.957 14.507 11.79 14.674 11.57 14.746C11.163 14.874 10.689 14.631 10.561 14.224L9.4 10.747C9.271 10.34 9.4 9.853 9.763 9.578C9.777 9.567 9.788 9.555 9.8 9.543C9.813 9.531 9.825 9.52 9.839 9.51L12 8.149L15.995 9.51Z"/>
    </svg>
);


function RepoMain() {
    const pageSubtitle = "Open Source & Research Projects | Building the Future of AI & Technology";
    const bannerImageUrl = "/images/BannerPage1.jpg";

    const [repos, setRepos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetch('https://dev.entrosystem.com/api/repos_api/list')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok. Please try again later.');
                }
                return response.json();
            })
            .then(data => {
                setRepos(data);
                setLoading(false);
            })
            .catch(error => {
                setError(error.message);
                setLoading(false);
            });
    }, []);

    const formatRepoName = (repo) => {
        return repo.name.replace('.git', '')
                   .replace(/_/g, ' ')
                   .replace(/\b\w/g, char => char.toUpperCase());
    };

    const getRepoDescription = (repo) => {
        // Use the description from the API response, fallback to default if none
        return repo.description && repo.description !== "No description available." 
            ? repo.description 
            : ' ';
    };

    const getRepoCategory = (repoName) => {
        const name = repoName.toLowerCase();
        if (name.includes('ai') || name.includes('neural') || name.includes('ml') || name.includes('llm')) return 'AI & Machine Learning';
        if (name.includes('vision') || name.includes('image')) return 'Computer Vision';
        if (name.includes('web') || name.includes('api') || name.includes('frontend') || name.includes('ui')) return 'Web Development';
        if (name.includes('robotics') || name.includes('control')) return 'Robotics';
        if (name.includes('hardware') || name.includes('embedded')) return 'Hardware';
        if (name.includes('physics') || name.includes('optics')) return 'Physics';
        if (name.includes('cuda') || name.includes('gpu')) return 'GPU Computing';
        if (name.includes('data') || name.includes('analysis')) return 'Data Science';
        if (name.includes('system') || name.includes('monitor')) return 'System Tools';
        if (name.includes('communication') || name.includes('comm') || name.includes('lora')) return 'Communication';
        if (name.includes('rust') || name.includes('c++') || name.includes('c')) return 'Programming';
        return 'Research';
    };
    
    // Loading State
    if (loading) {
        return (
            <div className={styles.pageContainer}>
                <Header backgroundImageUrl={bannerImageUrl} subtitle={pageSubtitle} />
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <div className={styles.loadingMessage}>Loading Repositories...</div>
                </div>
            </div>
        );
    }
    
    // Error State
    if (error) {
        return (
            <div className={styles.pageContainer}>
                <Header backgroundImageUrl={bannerImageUrl} subtitle={pageSubtitle} />
                <div className={styles.errorContainer}>
                    <h3 className={styles.errorTitle}>Repository Access Error</h3>
                    <p className={styles.errorMessage}>{error}</p>
                    <button 
                        className={styles.retryButton} 
                        onClick={() => window.location.reload()}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // Success State
    return (
        <div className={styles.pageContainer}>
            <Header backgroundImageUrl={bannerImageUrl} subtitle={pageSubtitle} />
            
            <main className={styles.mainContent}>
                {/* Introduction Section */}
                <section className={styles.introSection}>
                    <div className={styles.introContent}>
                        <h1 className={styles.pageTitle}>Open Source Portfolio</h1>
                        <p className={styles.pageDescription}>
                            A comprehensive collection of research projects, experimental frameworks, and 
                            production-ready solutions spanning artificial intelligence, hardware integration, 
                            and scientific computing. Each repository represents a commitment to transparency, 
                            innovation, and collaborative development.
                        </p>
                    </div>
                </section>

                {/* Repository Grid */}
                <section className={styles.repositoriesSection}>
                    <div className={styles.repoGrid}>
                        {repos.map(repo => (
                            <Link key={repo.name} to={`/repo-entry/${repo.name}`} className={styles.repoCard}>
                                <div className={styles.repoCardHeader}>
                                    <div className={styles.repoIconWrapper}>
                                        <RepoIcon />
                                    </div>
                                    <span className={styles.repoCategory}>{getRepoCategory(repo.name)}</span>
                                </div>
                                
                                <div className={styles.repoCardContent}>
                                    <h3 className={styles.repoName}>{formatRepoName(repo)}</h3>
                                    <p className={styles.repoDescription}>
                                        {getRepoDescription(repo)}
                                    </p>
                                </div>
                                
                                <div className={styles.repoCardFooter}>
                                    <div className={styles.repoMetrics}>
                                        <GitIcon />
                                        <span>Source Code</span>
                                    </div>
                                    <div className={styles.viewRepoLink}>
                                        <span>Explore Repository</span>
                                        <span className={styles.arrow}>→</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            </main>

            <ContactFooter 
                title="Open Source Collaboration"
                description="Interested in contributing, collaborating, or discussing these projects? Let's build something innovative together."
                copyrightText="© 2025 Entrosystem. All rights reserved."
                backgroundImageUrl={bannerImageUrl}
            />
        </div>
    );
}

export default RepoMain;